import { Link } from "react-router-dom";
import SiteFooter from "@/components/SiteFooter";
import type { AppMeta } from "@/lib/apps";
import { WAVLYNQ_LOGO } from "@/lib/apps";
import { trackEvent } from "@/lib/analytics/track";



interface AppShellProps {
  app: AppMeta;
}

/**
 * Shared per-app landing shell. Pre-launch placeholder — wired to the
 * locked theme tokens and brand assets so each app already feels distinct.
 * Real product surfaces (discovery, mentor, RAELA, etc.) land in later phases.
 */
const AppShell = ({ app }: AppShellProps) => {

  return (
    <div className={`min-h-screen flex flex-col bg-background text-foreground ${app.themeClass}`}>
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to={app.href} className="flex items-center" aria-label={`${app.name} home`}>
            <img src={app.logo} alt={app.name} className="h-7 w-auto" />
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#safety" className="text-muted-foreground hover:text-foreground transition-colors">Safety</a>
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
              <img src={WAVLYNQ_LOGO} alt="" className="h-4 w-auto" aria-hidden />
              <span>Network</span>
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <section className="relative overflow-hidden">
          <div className={`absolute inset-x-0 top-0 h-1 ${app.gradientClass}`} aria-hidden />
          <div className={`absolute -top-40 -right-40 h-[28rem] w-[28rem] rounded-full opacity-20 blur-3xl ${app.gradientClass}`} aria-hidden />

          <div className="relative mx-auto max-w-6xl px-6 pt-24 pb-20 md:pt-32 md:pb-28">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
              {app.community} · 18+
            </p>
            <img src={app.logo} alt={`${app.name} logo`} className="mt-6 h-12 md:h-14 w-auto" />

            <h1 className="mt-8 text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] max-w-3xl">
              {app.tagline}
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              {app.description}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to={`/welcome/${app.id}`}
                onClick={() => trackEvent("cta_get_started", { app: app.id })}
                className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Get started
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-secondary transition-colors"
              >
                See all 9 apps
              </Link>
            </div>
          </div>
        </section>

        <section id="features" className="border-t border-border bg-secondary/30">
          <div className="mx-auto max-w-6xl px-6 py-20 grid gap-10 md:grid-cols-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Discovery</p>
              <p className="mt-3 text-lg font-semibold">Matches that actually fit.</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Priority-aware discovery built around what matters to {app.community.toLowerCase()}.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Mentorship</p>
              <p className="mt-3 text-lg font-semibold">Verified, background-checked mentors.</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Guidance from people who've been where you are.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">RAELA</p>
              <p className="mt-3 text-lg font-semibold">An always-on companion.</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Crisis-aware, community-tuned, available the moment you need it.
              </p>
            </div>
          </div>
        </section>

        <section id="safety" className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-16 max-w-3xl">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Safety</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
              Built in. Not bolted on.
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Emergency Button, Crisis Resources, ID verification, and a
              crisis-aware RAELA — present in {app.name} the same way they're
              present in every Wavlynq app.
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default AppShell;
